import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, type FC } from 'react';

import type { Props } from '@/components/EligibilityCriteriaBox/types';
import { ELIGIBILITY_APPLICATION_KEY } from '@/constants/treatmentGroups';
import { useLogicApplication, useLogicFieldOptions } from '@/hooks/useLogicApplication';
import { useLogicGroups } from '@/hooks/useLogicGroups';
import { LogicOperator } from '@/types/logic';
import { Typography } from '@/ui';
import {
  buildEligibilityCriteriaTree,
  type CriteriaContent
} from '@/utils/eligibilityCriteriaTree';

const OPERATOR_LABELS: Record<LogicOperator, string> = {
  [LogicOperator.AND]: 'All of',
  [LogicOperator.OR]: 'Any of'
};

const CriteriaClassificationPill: FC<{
  content: Extract<CriteriaContent, { type: 'classification' }>;
}> = ({ content }) =>
  content.label ? (
    <span className="inline-flex items-stretch overflow-hidden rounded-full border border-amber-200 dark:border-amber-800">
      <span className="bg-amber-100/70 px-3 py-1 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
        {content.label}
      </span>
      <span className="bg-white px-3 py-1 text-xs font-semibold text-uniform-gray-900 dark:bg-uniform-gray-900 dark:text-uniform-gray-100">
        {content.value}
      </span>
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-medium text-uniform-gray-900 dark:border-amber-800 dark:bg-uniform-gray-900 dark:text-uniform-gray-100">
      {content.value}
    </span>
  );

const CriteriaContentView: FC<{ content: CriteriaContent }> = ({ content }) => {
  if (content.type === 'classification') return <CriteriaClassificationPill content={content} />;

  return (
    <div className="space-y-2">
      <Typography variant="label" weight="bold" className="text-amber-700 dark:text-amber-400">
        {OPERATOR_LABELS[content.operator]}
      </Typography>
      <div className="space-y-2 pl-3">
        {content.parts.map((part, index) => (
          <CriteriaContentView key={index} content={part} />
        ))}
      </div>
    </div>
  );
};

const EligibilityCriteriaBox: FC<Props> = ({ excludeGroup, subjectId }) => {
  const { data: eligibilityApplication } = useLogicApplication(ELIGIBILITY_APPLICATION_KEY);
  const { data: rootGroups } = useLogicGroups(eligibilityApplication?.id, subjectId);
  const { data: fieldOptions } = useLogicFieldOptions(eligibilityApplication?.id);

  const content = useMemo(() => {
    const relevantGroups = excludeGroup
      ? (rootGroups ?? []).filter((group) => !excludeGroup(group))
      : (rootGroups ?? []);
    return buildEligibilityCriteriaTree(relevantGroups, fieldOptions ?? []);
  }, [rootGroups, fieldOptions, excludeGroup]);

  if (!content) return null;

  return (
    <div
      data-testid="eligibility-criteria-box"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-3 flex items-center gap-2 text-amber-800 dark:text-amber-300">
        <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />
        <Typography variant="label" weight="bold" className="text-amber-800 dark:text-amber-300">
          Additional eligibility criteria
        </Typography>
      </div>
      <CriteriaContentView content={content} />
    </div>
  );
};

export default EligibilityCriteriaBox;
